import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, ValidationErrors } from '@angular/forms';
import { enumDocumentState, enumDocumentType } from '@app/data-layer/enums/documents';
import { DocumentModel } from '@app/data-layer/models/documents';
import { DocumentsRepository } from '@app/data-layer/repositories';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { enumMimeType } from '@enum/enumMimeType';
import { environment } from '@env/environment';
import { IDefaultSelect } from '@interfaces/default-select';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject, Subscription, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-document-upload-preview',
  templateUrl: './document-upload-preview.component.html',
  styleUrls: ['./document-upload-preview.component.scss'],
})
export class DocumentUploadPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;

  @Input() public id: string;
  @Input() public type: 'standard' | 'multiple' = 'standard';
  @Input() public changeCounter: number;
  @Input() public uploadedDocument: DocumentModel;
  @Input() public documentPreviewUrl: string;
  @Input() public isLoading: boolean;
  @Input() public isClickOnly: boolean;
  @Input() public tagText: string = 'LABELS.OPTIONAL';
  @Input() public tagTooltip: string = '';
  @Input() public uploadingStatusText: string = 'LABELS.UPLOADING';
  @Input() public labelText: string;
  @Input() public tagType: 'alert' | 'warning' | '' = '';
  @Input() public multipleFileOptions: IDefaultSelect[];
  @Input() public fileMimeIgnoreList: enumMimeType[] = [];
  @Input() public fileExtensionIgnoreList: enumFileExtension[] = [];
  @Input() public multipleDocSelectTypes: enumDocumentType[] = [];
  @Input() public isDragOver: boolean = false;
  @Input() public showDeleteWhenEmpty: boolean = false;
  @Input() public canDelete: boolean = true;

  @Output() public click: EventEmitter<void> = new EventEmitter<void>();
  @Output() public upload: EventEmitter<File | { file: File; type: enumDocumentType }> = new EventEmitter<
    File | { file: File; type: enumDocumentType }
  >();
  @Output() public replace: EventEmitter<void> = new EventEmitter<void>();
  @Output() public fail: EventEmitter<void> = new EventEmitter<void>();
  @Output() public delete: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();
  @Output() public stateUpdate: EventEmitter<enumDocumentState> = new EventEmitter<enumDocumentState>();
  @Output() public preview: EventEmitter<void> = new EventEmitter<void>();
  @Output() public multipleDocTypeSelect: EventEmitter<enumDocumentType> = new EventEmitter<enumDocumentType>();

  public isUploadingDoc: boolean;
  public isShowMultipleFileOptions: boolean;
  public isShowInvalidFileToast: boolean = false;
  public allowedFileMimes: string = '';
  public fileControl: UntypedFormControl = new UntypedFormControl(null);

  public errorMessage: string = '';

  private isElementClicked = false;

  private currentMultipleOptionsFileType: enumDocumentType;

  private checkDocumentStateSubscription: Subscription;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private documentsRepository: DocumentsRepository,
  ) {}

  @HostListener('click')
  clickedInsideElement() {
    this.isElementClicked = true;
  }

  @HostListener('document:click')
  clickedOutsideElement() {
    if (!this.isElementClicked) {
      this.setIsShowMultipleFileOptions(false);
    }
    this.isElementClicked = false;
  }

  ngOnInit(): void {
    this.allowedFileMimes = ALLOWED_FILE_MIMES.filter((fm) => !this.fileMimeIgnoreList.includes(fm)).join(',');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (!this.checkDocumentStateSubscription || this.checkDocumentStateSubscription.closed) &&
      this.uploadedDocument?.state === enumDocumentState.SCAN_PENDING
    ) {
      this.checkDocumentState();
    }

    if (!!changes.uploadedDocument && !!this.uploadedDocument?.id && this.isUploadingDoc) {
      this.isUploadingDoc = this.uploadedDocument?.state === enumDocumentState.SCAN_PENDING;
    }

    if (changes.changeCounter && !changes.changeCounter.firstChange) {
      if (changes.changeCounter.currentValue !== changes.changeCounter.previousValue) {
        this.setIsShowInvalidFileToast(false);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public uploadClick() {
    if (this.isClickOnly) {
      this.click.emit();
      return;
    }

    if (this.isLoading || this.isUploadingDoc || this.isDocumentScanPending) {
      return;
    }

    switch (this.type) {
      case 'standard':
      default:
        this.inputElementClick();
        break;
      case 'multiple':
        this.setIsShowMultipleFileOptions(true);
        break;
    }
  }

  public multipleFileUploadClick(type: enumDocumentType) {
    if (this.multipleDocSelectTypes.includes(type)) {
      this.multipleDocTypeSelect.emit(type);
      return;
    }

    this.currentMultipleOptionsFileType = type;
    this.inputElementClick();
  }

  public inputElementClick() {
    this.inputElement.nativeElement.click();
  }

  public clickInput(): void {
    this.clearFile();
  }

  public handleFiles(event: Event): void {
    if (this.isClickOnly) {
      return;
    }

    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.isUploadingDoc = true;
      this.uploadFile(files[0]);
    }
  }

  public onPreviewDocument() {
    this.preview.emit();
  }

  public onDelete(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.clearFile();
    this.delete.emit(this.uploadedDocument);
  }

  public get fileName(): string {
    return this.uploadedDocument && this.uploadedDocument.fileName ? this.uploadedDocument.fileName : null;
  }

  public get isDocumentScanPending(): boolean {
    return this.uploadedDocument?.state === enumDocumentState.SCAN_PENDING;
  }

  public get isDocumentUploaded(): boolean {
    return [enumDocumentState.UPLOADED, enumDocumentState.CREATED].includes(this.uploadedDocument?.state);
  }

  public get shouldShowDeleteButton(): boolean {
    return this.canDelete && this.showDeleteWhenEmpty && !(this.isLoading || this.isUploadingDoc || this.isDocumentScanPending);
  }

  public onCloseInvalidFileToast() {
    this.setIsShowInvalidFileToast(false);
  }

  public onReplace() {
    this.setIsShowInvalidFileToast(false);
    this.replace.emit();
    if (this.type === 'multiple') {
      setTimeout(() => {
        this.uploadClick();
        this.cdr.detectChanges();
      }, 0);
    }
  }

  private uploadFile(file: File): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();

    this.updateInputErrors(file);

    if (this.fileControl.valid) {
      switch (this.type) {
        case 'standard':
        default:
          this.upload.emit(file);
          this.isUploadingDoc = false;
          break;
        case 'multiple':
          this.upload.emit({ file, type: this.currentMultipleOptionsFileType });
          this.isUploadingDoc = false;
          this.setIsShowMultipleFileOptions(false);
          break;
      }
    } else {
      this.fail.emit();
      this.setIsShowInvalidFileToast(true);
      this.setIsShowMultipleFileOptions(false);
      this.isUploadingDoc = false;
    }
    this.inputElement.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private updateInputErrors(file: File) {
    const allowedFileExtensions = ALLOWED_FILE_EXTENSIONS.filter((fe) => !this.fileExtensionIgnoreList.includes(fe));
    const extensionError = this.fileExtensionsValidator(file, allowedFileExtensions);
    const sizeError = this.maxFileSizeValidator(file, environment.files.default_file_size_limit); // 10 MB limit

    if (extensionError) {
      this.fileControl.setErrors(extensionError);
      this.errorMessage = this.translateService.instant('FORM_ERROR.FILE_EXTENSION_NOT_SUPPORTED', { fileName: file.name });
    } else if (sizeError) {
      this.fileControl.setErrors(sizeError);
      this.errorMessage = this.translateService.instant('FORM_ERROR.FILE_SIZE_EXCEEDS_LIMIT', { fileName: file.name });
    } else {
      this.fileControl.setValue(file);
      this.fileControl.setErrors(null);
      this.errorMessage = '';
    }
  }

  private checkDocumentState(): void {
    const timerVal: number = 1000;

    if (this.checkDocumentStateSubscription) {
      this.checkDocumentStateSubscription.unsubscribe();
    }
    this.checkDocumentStateSubscription = timer(timerVal)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.documentsRepository.getDocumentState(this.uploadedDocument.id, false)),
      )
      .subscribe((state: enumDocumentState) => {
        if (state !== enumDocumentState.SCAN_PENDING) {
          this.stateUpdate.emit(state);
          this.isUploadingDoc = false;
        } else {
          this.checkDocumentState();
        }
      });
  }

  private setIsShowMultipleFileOptions(value: boolean) {
    this.isShowMultipleFileOptions = value;
  }

  private setIsShowInvalidFileToast(value: boolean) {
    this.isShowInvalidFileToast = value;
  }

  private fileExtensionsValidator(file: File, allowedExtensions: string[]): ValidationErrors | null {
    const extension = file.name.split('.').pop()?.toLowerCase() as string;

    return allowedExtensions.includes(extension) ? null : { fileExtensionNotAllowed: true };
  }

  private maxFileSizeValidator(file: File, maxFileSizeMB: number): ValidationErrors | null {
    const fileSizeMB = file.size / 1024 / 1024;

    return fileSizeMB > maxFileSizeMB
      ? { maxFileSize: { currentSize: Math.round(fileSizeMB * 100) / 100, maxFileSize: maxFileSizeMB } }
      : null;
  }
}
